import { PUBLISH_LIMITS, type SubmissionView } from '@mogzo/shared';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { ApiError, apiSend, PLAYER_SITE, type Project } from '../lib/api';
import { useApi } from '../lib/use-api';
import { StatusChip, when } from './submissions';
import { Alert, Button, cardClass, fieldClass } from './ui';

// Publishing on the Submissions page (M23): the approved version waiting to go live, and the
// live one.

const hint = 'text-[13px] text-muted';

interface GoingLive {
  against: string | null;
  changedFiles: number;
  setup: {
    kind: 'leaderboard' | 'achievement';
    name: string;
    change: 'added' | 'changed' | 'removed';
  }[];
  listing: string[];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "India Standard Time (UTC+5:30)": the time zone a set time is in. */
function zoneName(): string {
  const minutes = -new Date().getTimezoneOffset();
  const sign = minutes < 0 ? '−' : '+';
  const hours = Math.floor(Math.abs(minutes) / 60);
  const rest = Math.abs(minutes) % 60;
  const offset = `UTC${sign}${hours}${rest ? `:${String(rest).padStart(2, '0')}` : ''}`;
  const name = new Intl.DateTimeFormat('en-GB', { timeZoneName: 'long' })
    .formatToParts(new Date())
    .find((part) => part.type === 'timeZoneName')?.value;
  return name ? `${name} (${offset})` : offset;
}

const pad = (n: number) => String(n).padStart(2, '0');
const dateValue = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const timeValue = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

/** What players get when it goes live, against the live version. */
function Changes({ submission }: { submission: SubmissionView }) {
  const result = useApi<GoingLive>(`/v1/dev/submissions/${submission.id}/going-live`);
  if (result.status !== 'ready') return null;
  const { against, changedFiles, setup, listing } = result.data;
  const items: [string, string][] = [
    submission.kind === 'listing'
      ? [`The game stays on ${submission.version}`, 'Same build, leaderboards and achievements.']
      : against
        ? [
            `Build #${submission.buildNumber} replaces ${against}`,
            `${plural(changedFiles, 'file')} changed. Players already in the game keep ${against} until they reload.`,
          ]
        : [`Build #${submission.buildNumber}`, 'The first version players get.'],
    ...setup.map(({ kind, name, change }): [string, string] => [
      `${change === 'added' ? 'New' : change === 'removed' ? 'Removed' : 'Changed'} ${kind}: ${name}`,
      change === 'removed'
        ? 'Players’ scores or unlocks on it are kept, but hidden.'
        : 'Live gets it as the reviewer saw it.',
    ]),
    listing.length
      ? [
          `Store listing: ${plural(listing.length, 'field')}`,
          `${listing.join(', ')}, as ${submission.kind === 'listing' ? 'sent for review' : `sent with ${submission.version}`}. Later edits stay a draft.`,
        ]
      : ['Store listing', `As sent with ${submission.version}.`],
  ];
  return (
    <div>
      <p className="mb-2.5 text-xs font-semibold tracking-wide text-muted uppercase">
        What players get when it goes live
      </p>
      <ul className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3">
        {items.map(([title, detail]) => (
          <li
            key={title}
            className="flex flex-col gap-1 rounded-[6px] border border-divider px-3.5 py-3"
          >
            <strong className="text-sm">{title}</strong>
            <span className={hint}>{detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Dialog({
  title,
  onSubmit,
  children,
}: {
  title: string;
  onSubmit: (event: FormEvent) => void;
  children: React.ReactNode;
}) {
  const id = useId();
  return (
    <div className="fixed inset-0 z-10 flex items-start justify-center overflow-y-auto bg-[var(--overlay)] px-4 py-24">
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        onSubmit={onSubmit}
        className="flex w-full max-w-[520px] flex-col gap-4 rounded-[20px] border border-line bg-card p-7"
      >
        <h2 id={id} className="text-[22px] font-semibold">
          {title}
        </h2>
        {children}
      </form>
    </div>
  );
}

/** The approved version waiting to go live: publish it now, at a set time, or wait for it. */
export function Waiting({
  project,
  submission,
  live,
  onChange,
}: {
  project: Project;
  submission: SubmissionView;
  live: SubmissionView | undefined;
  onChange: () => void;
}) {
  const headingId = useId();
  const [dialog, setDialog] = useState<'now' | 'later' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const soonest = new Date(Date.now() + (PUBLISH_LIMITS.leadMinutes + 5) * 60_000);
  const start = submission.publishAt ? new Date(submission.publishAt) : soonest;
  const [date, setDate] = useState(dateValue(start));
  const [time, setTime] = useState(timeValue(start));
  const publishAt = submission.publishAt ? new Date(submission.publishAt) : null;
  const going = publishAt !== null && publishAt.getTime() <= Date.now();

  // Going live takes a moment: look again until it's live.
  useEffect(() => {
    if (!going) return;
    const timer = setInterval(onChange, 4000);
    return () => clearInterval(timer);
  }, [going, onChange]);

  async function send(method: 'POST' | 'DELETE', body?: { at: string | null }) {
    setBusy(true);
    setError(null);
    try {
      await apiSend(method, `/v1/dev/submissions/${submission.id}/publish`, body);
      setDialog(null);
      onChange();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const suspended = Boolean(project.suspendedAt);
  const listingOnly = submission.kind === 'listing';
  return (
    <section
      aria-labelledby={headingId}
      className={`${cardClass} flex flex-col gap-[18px] p-6 ${going || publishAt ? '' : 'border-success'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className={`${hint} font-semibold`}>
            {going ? 'Going live now' : publishAt ? 'Scheduled' : 'Ready to publish'}
          </p>
          <h3 id={headingId} className="mt-1 text-[22px] font-semibold">
            {publishAt && !going ? (
              <>
                {listingOnly ? 'The store listing change' : `Version ${submission.version}`} goes
                live {when(publishAt.toISOString(), { at: true })}
              </>
            ) : listingOnly ? (
              <>
                Store listing change{' '}
                <span className="font-medium text-muted">· game stays on {submission.version}</span>
              </>
            ) : (
              <>
                Version {submission.version}{' '}
                <span className="font-medium text-muted">· build #{submission.buildNumber}</span>
              </>
            )}
          </h3>
          <p className={`${hint} mt-1`}>
            {publishAt && !going
              ? `Your time, ${zoneName()}. Until then players get ${live?.version ?? 'nothing'}.`
              : `Approved ${submission.decidedAt ? when(submission.decidedAt) : ''}.`}
            {submission.review?.message && !publishAt
              ? ` Reviewer’s message: “${submission.review.message}”`
              : ''}
          </p>
        </div>
        <StatusChip status={going ? 'approved' : publishAt ? 'scheduled' : 'approved'} />
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      {going ? (
        <p role="status" className={`${hint} rounded-[6px] bg-field px-3.5 py-2.5`}>
          Copying the build and setting up live. This takes a minute or two.
        </p>
      ) : (
        <>
          {!publishAt && <Changes submission={submission} />}
          {!publishAt && !listingOnly && (
            <p className={`${hint} rounded-[6px] bg-field px-3.5 py-2.5`}>
              Live data stays: scores, saves, achievements players have and game data carry over.
            </p>
          )}
          {suspended ? (
            <p className={hint}>Nothing can be published while the game is suspended.</p>
          ) : (
            <div className="flex flex-wrap gap-2.5">
              {publishAt ? (
                <>
                  <Button variant="secondary" onClick={() => setDialog('later')}>
                    Change time
                  </Button>
                  <Button variant="secondary" disabled={busy} onClick={() => send('DELETE')}>
                    Cancel schedule
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => setDialog('now')}>Publish now</Button>
                  <Button variant="secondary" onClick={() => setDialog('later')}>
                    Publish at a set time
                  </Button>
                </>
              )}
            </div>
          )}
        </>
      )}

      {dialog === 'now' && (
        <Dialog
          title={
            listingOnly
              ? `Publish ${project.name}’s listing change now?`
              : `Publish ${project.name} ${submission.version} now?`
          }
          onSubmit={(event) => {
            event.preventDefault();
            void send('POST', { at: null });
          }}
        >
          <p className="leading-relaxed text-muted">
            {listingOnly
              ? `The game page shows the new listing from now on. The game stays on ${submission.version}.`
              : `${
                  live
                    ? `It replaces ${live.version} for everyone who opens the game from now on.`
                    : 'It becomes the game players get.'
                } Its store listing, leaderboards and achievements go live with it, and live data stays.`}
          </p>
          <p className={`${hint} rounded-[6px] bg-field px-3.5 py-2.5`}>
            {live
              ? `If something goes wrong, MOGZO can roll the game back to ${live.version}. Write to hello@mogzo.com.`
              : 'If something goes wrong, write to hello@mogzo.com.'}
          </p>
          <div className="flex flex-wrap justify-end gap-2.5">
            <Button variant="secondary" onClick={() => setDialog(null)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Publish now
            </Button>
          </div>
        </Dialog>
      )}
      {dialog === 'later' && (
        <Dialog
          title={`When should ${submission.version} go live?`}
          onSubmit={(event) => {
            event.preventDefault();
            const at = new Date(`${date}T${time}`);
            if (Number.isNaN(at.getTime())) {
              setError('Pick a date and a time.');
              return;
            }
            void send('POST', { at: at.toISOString() });
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1.5 text-sm font-semibold">
              Date
              <input
                type="date"
                required
                value={date}
                min={dateValue(new Date())}
                onChange={(event) => setDate(event.target.value)}
                className={fieldClass}
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm font-semibold">
              Time
              <input
                type="time"
                required
                value={time}
                onChange={(event) => setTime(event.target.value)}
                className={fieldClass}
              />
            </label>
          </div>
          <p className={hint}>
            Your time: {zoneName()}. Pick a time from {PUBLISH_LIMITS.leadMinutes} minutes to{' '}
            {PUBLISH_LIMITS.aheadDays} days from now.
          </p>
          {error && <Alert tone="error">{error}</Alert>}
          <p className={`${hint} rounded-[6px] bg-field px-3.5 py-2.5`}>
            You can change the time or cancel until it goes live. We email you when it does.
          </p>
          <div className="flex flex-wrap justify-end gap-2.5">
            <Button
              variant="secondary"
              onClick={() => {
                setDialog(null);
                setError(null);
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Schedule
            </Button>
          </div>
        </Dialog>
      )}
    </section>
  );
}

/** The version players get now. */
export function LiveNow({ project, submission }: { project: Project; submission: SubmissionView }) {
  const headingId = useId();
  return (
    <section
      aria-labelledby={headingId}
      className={`${cardClass} flex flex-wrap items-center justify-between gap-3.5 px-6 py-5`}
    >
      <div>
        <p className={`${hint} font-semibold`}>Live now</p>
        <h3 id={headingId} className="mt-1 text-[15px] font-semibold">
          Version {submission.version}{' '}
          <span className="font-medium text-muted">
            · build #{submission.buildNumber}
            {submission.kind === 'listing' ? ' · with a store listing change' : ''}
            {submission.publishedAt ? ` · published ${when(submission.publishedAt)}` : ''}
          </span>
        </h3>
        <p className={`${hint} mt-1`}>
          Until MOGZO opens to players, only you and MOGZO staff can play it.
        </p>
      </div>
      <a
        href={`${PLAYER_SITE}/games/${project.slug}`}
        className="inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-card px-5 text-sm font-semibold hover:border-muted"
      >
        Open on mogzo.com
      </a>
    </section>
  );
}
